import { useState, useRef, useEffect, useMemo, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { Funnel, MagnifyingGlass, CaretUp, CaretDown } from "@phosphor-icons/react";

// Sentinel token: stored in the `selected` set to represent the "all
// deselected" state (i.e. show no rows). No real row value should ever
// collide with this string.
const NONE_SENTINEL = "__COLFILTER_NONE__";

/**
 * Excel-like column filter.
 *
 * Props:
 *   values: Array of strings (unique). Optionally an array of { value, label }.
 *   selected: Set<string>.
 *             - EMPTY set → "no filter, show all rows"
 *             - Set containing NONE_SENTINEL → "everything deselected, show no rows"
 *             - Non-empty set of real values → show ONLY rows whose value is in the set
 *   onChange: (Set<string>) => void
 *   testid: string
 *   align: "left" | "right" – popover alignment (default "left")
 */
export default function ColumnFilter({ values, selected, onChange, testid, align = "left" }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  // Recompute popover position from the trigger's bounding rect.
  // Uses `position: fixed` so the popover escapes any overflow-hidden/auto ancestor.
  const POPOVER_WIDTH = 320; // matches Tailwind w-80
  const POPOVER_MAX_HEIGHT = 384; // matches max-h-96
  const computePosition = () => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = align === "right" ? r.right - POPOVER_WIDTH : r.left;
    // Clamp within viewport with a 8px gutter
    left = Math.max(8, Math.min(left, vw - POPOVER_WIDTH - 8));
    let top = r.bottom + 4;
    // Flip above if there isn't enough room below
    if (top + POPOVER_MAX_HEIGHT + 8 > vh && r.top > POPOVER_MAX_HEIGHT + 8) {
      top = r.top - POPOVER_MAX_HEIGHT - 4;
    }
    setPos({ top, left });
  };

  useLayoutEffect(() => {
    if (open) computePosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => computePosition();
    const onDown = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (popRef.current && popRef.current.contains(e.target)) return;
      setOpen(false);
    };
    // Capture-phase scroll listener catches ancestor scrolls too.
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    document.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
      document.removeEventListener("mousedown", onDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const normalized = useMemo(
    () => values.map((v) => (typeof v === "object" ? v : { value: v, label: String(v) })),
    [values]
  );
  const allValues = useMemo(() => normalized.map((v) => v.value), [normalized]);
  const noneSelected = selected.has(NONE_SENTINEL);
  const active = noneSelected || (selected.size > 0 && selected.size < allValues.length);

  const filtered = search
    ? normalized.filter((v) => (v.label || "").toLowerCase().includes(search.toLowerCase()))
    : normalized;

  const isChecked = (val) => {
    if (noneSelected) return false;
    if (selected.size === 0) return true;
    return selected.has(val);
  };

  const toggle = (val) => {
    let next;
    if (noneSelected) {
      // Currently everything is deselected: clicking a value starts a fresh
      // single-value selection.
      next = new Set([val]);
    } else if (selected.size === 0) {
      // All implicitly checked → unchecking one means: all except this.
      next = new Set(allValues.filter((x) => x !== val));
    } else {
      next = new Set(selected);
      if (next.has(val)) next.delete(val);
      else next.add(val);
    }
    // Normalize edge states
    if (next.size === 0) {
      next = new Set([NONE_SENTINEL]);        // everything unchecked → hide all rows
    } else if (next.size === allValues.length && !next.has(NONE_SENTINEL)) {
      next = new Set();                        // everything checked → no filter
    }
    onChange(next);
  };

  const clearFilter = () => onChange(new Set());              // "Mostra tutti"
  const deselectAll = () => onChange(new Set([NONE_SENTINEL])); // "Deseleziona tutti"
  const selectOnlyVisible = () => onChange(new Set(filtered.map((v) => v.value)));

  return (
    <div className="relative inline-block align-middle">
      <button
        ref={btnRef}
        type="button"
        data-testid={testid}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className={`p-1 hover:bg-neutral-200 transition-colors ${active ? "text-[#002FA7]" : "text-neutral-400"}`}
        title="Filtra colonna"
      >
        <Funnel size={12} weight={active ? "fill" : "regular"} />
      </button>
      {open && createPortal(
        <div
          ref={popRef}
          style={{ position: "fixed", top: pos.top, left: pos.left, width: POPOVER_WIDTH, maxHeight: POPOVER_MAX_HEIGHT }}
          className="z-[100] bg-white border border-neutral-300 shadow-xl flex flex-col normal-case"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-2 border-b border-neutral-200 flex items-center gap-2 bg-neutral-50">
            <MagnifyingGlass size={14} className="text-neutral-400" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca valore..."
              className="flex-1 outline-none text-xs bg-transparent"
              data-testid={testid ? `${testid}-search` : undefined}
            />
          </div>
          <div className="px-2 py-1.5 border-b border-neutral-200 flex items-center justify-between gap-2 text-[10px] uppercase tracking-wider">
            <button
              type="button"
              onClick={clearFilter}
              className="text-[#002FA7] hover:underline font-medium normal-case"
              data-testid={testid ? `${testid}-clear` : undefined}
              title="Seleziona tutti i valori (rimuove il filtro)"
            >
              Mostra tutti
            </button>
            <button
              type="button"
              onClick={deselectAll}
              className="text-red-600 hover:underline font-medium normal-case"
              data-testid={testid ? `${testid}-deselect-all` : undefined}
              title="Deseleziona tutti i valori (nessuna riga visibile)"
            >
              Deseleziona tutti
            </button>
            {search && (
              <button
                type="button"
                onClick={selectOnlyVisible}
                className="text-emerald-700 hover:underline font-medium normal-case"
                data-testid={testid ? `${testid}-only-visible` : undefined}
              >
                Solo visibili
              </button>
            )}
            <span className="text-neutral-400 normal-case ml-auto">
              {noneSelected
                ? "0 attivi"
                : selected.size
                  ? `${selected.size} attivi`
                  : "tutti"}
            </span>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-4 text-center text-xs text-neutral-500">Nessun valore.</div>
            ) : filtered.map((v) => (
              <label
                key={String(v.value)}
                className="flex items-start gap-2 px-2 py-1.5 hover:bg-neutral-50 cursor-pointer text-xs border-b border-neutral-100 last:border-b-0"
                title={v.label || "(vuoto)"}
              >
                <input
                  type="checkbox"
                  checked={isChecked(v.value)}
                  onChange={() => toggle(v.value)}
                  className="w-3.5 h-3.5 accent-[#002FA7] shrink-0 mt-0.5"
                />
                <span className="flex-1 break-words leading-snug">
                  {v.label || <em className="text-neutral-400">(vuoto)</em>}
                </span>
              </label>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

/**
 * Utility: given rows and a map { columnKey: Set<value> }, return only rows
 * that pass every active column filter. Empty sets are treated as "no filter".
 * `accessors` is a map of columnKey -> (row) => value string.
 */
export function applyColumnFilters(rows, filters, accessors) {
  const activeKeys = Object.keys(filters).filter((k) => filters[k] && filters[k].size > 0);
  if (activeKeys.length === 0) return rows;
  return rows.filter((row) =>
    activeKeys.every((k) => {
      const set = filters[k];
      const val = accessors[k](row);
      return set.has(val);
    })
  );
}

/**
 * Dynamic uniques: for every column key, return the unique values that appear
 * in rows AFTER applying every OTHER active filter (i.e. excluding the column's
 * own filter). Mimics Excel/Google Sheets behavior: opening a filter only
 * exposes values that are still reachable given the active filters on the
 * other columns.
 *
 *   rows       – full unfiltered rows
 *   filters    – map { columnKey: Set<value> } (may include NONE_SENTINEL)
 *   accessors  – map { columnKey: (row) => stringValue }
 *   keyToUnique – map { columnKey: (rows, accessor) => Array<{value,label}> }
 *                 (optional per-column formatter; defaults to uniqueValues)
 */
export function dynamicUniques(rows, filters, accessors, keyToUnique = {}) {
  const out = {};
  for (const key of Object.keys(accessors)) {
    const others = { ...filters, [key]: new Set() };
    const base = applyColumnFilters(rows, others, accessors);
    const fn = keyToUnique[key] || uniqueValues;
    out[key] = fn(base, accessors[key]);
  }
  return out;
}

/**
 * Utility: extract unique values from an array of rows.
 * Returns array of { value, label } sorted alphabetically by label,
 * with empty string as "(vuoto)".
 */
export function uniqueValues(rows, accessor) {
  const seen = new Map();
  for (const r of rows) {
    const v = accessor(r);
    const key = v == null ? "" : String(v);
    if (!seen.has(key)) seen.set(key, key);
  }
  return Array.from(seen.keys())
    .sort((a, b) => a.localeCompare(b, "it", { sensitivity: "base" }))
    .map((k) => ({ value: k, label: k || "(vuoto)" }));
}

/**
 * Clickable sort header. Cycles: null -> asc -> desc -> null.
 *
 * Props:
 *   label: string – header text
 *   sortKey: string – key in accessors map
 *   sortConfig: { key, dir } | null
 *   setSortConfig: (config|null) => void
 *   testid: optional
 *   align: "left" (default) | "right" – used for numeric right-aligned columns
 */
export function SortableHeader({ label, sortKey, sortConfig, setSortConfig, testid, align = "left", children }) {
  const active = sortConfig && sortConfig.key === sortKey;
  const dir = active ? sortConfig.dir : null;
  const cycle = () => {
    if (!active) setSortConfig({ key: sortKey, dir: "asc" });
    else if (dir === "asc") setSortConfig({ key: sortKey, dir: "desc" });
    else setSortConfig(null);
  };
  const justify = align === "right" ? "justify-end" : "justify-start";
  let title = "Ordina crescente";
  if (active && dir === "asc") title = "Ordinato crescente – clicca per decrescente";
  else if (active) title = "Ordinato decrescente – clicca per rimuovere";
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={cycle}
      title={title}
      className={`inline-flex items-center gap-1 w-full ${justify} hover:text-[#002FA7] transition-colors ${active ? "text-[#002FA7]" : ""}`}
    >
      <span>{label}</span>
      {dir === "asc" && <CaretUp size={10} weight="bold" data-testid={testid ? `${testid}-asc` : undefined} />}
      {dir === "desc" && <CaretDown size={10} weight="bold" data-testid={testid ? `${testid}-desc` : undefined} />}
      {children}
    </button>
  );
}

/**
 * Sort rows given sortConfig and accessors map.
 * Numeric values compared numerically; strings via localeCompare (Italian, numeric-aware).
 * Null/undefined values sorted to the end.
 */
export function sortRows(rows, sortConfig, accessors) {
  if (!sortConfig || !sortConfig.key) return rows;
  const acc = accessors[sortConfig.key];
  if (!acc) return rows;
  const sign = sortConfig.dir === "desc" ? -1 : 1;
  const copy = [...rows];
  copy.sort((a, b) => {
    const va = acc(a);
    const vb = acc(b);
    if (va == null && vb == null) return 0;
    if (va == null || va === "") return 1;
    if (vb == null || vb === "") return -1;
    if (typeof va === "number" && typeof vb === "number") return (va - vb) * sign;
    return String(va).localeCompare(String(vb), "it", { sensitivity: "base", numeric: true }) * sign;
  });
  return copy;
}
